import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/carte-fidelite-numerique/couverture.webp";
import outilsImage from "@/assets/blog/carte-fidelite-numerique/outils-creation-vs-plateformes-wallet.webp";
import notificationImage from "@/assets/blog/carte-fidelite-numerique/notification-wallet-ecran-verrouille-preference.webp";
import etapesImage from "@/assets/blog/carte-fidelite-numerique/creer-carte-fidelite-numerique-etapes.webp";
import erreursImage from "@/assets/blog/carte-fidelite-numerique/erreurs-adoption-carte-fidelite-numerique.webp";

const CarteFideliteNumerique = () => {
  const title = "Carte de fidélité numérique : le guide complet pour la créer et la déployer en 2026";
  const jsonLdSchemas = [
    generateWebPageSchema(title, seoConfig.blogCarteFideliteNumerique.description, "/carte-fidelite-numerique"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Carte de fidélité numérique", path: "/carte-fidelite-numerique" }
    ]),
    generateArticleSchema(title, seoConfig.blogCarteFideliteNumerique.description, "https://unotif.com/carte-fidelite-numerique", "2026-06-16")
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  const pText = "text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4";
  const h2 = "text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12";
  const linkCls = "text-[#0c97a9] hover:underline";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogCarteFideliteNumerique} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className={linkCls}>Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Carte de fidélité numérique</span>
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              {title}
            </h1>

            <img
              src={coverImage}
              alt="Fonctionnement d’une carte de fidélité digitale sur smartphone avec stockage wallet et utilisation en magasin"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Intro */}
            <p className={pText}>
              Une carte en papier se froisse dans un portefeuille. Elle se perd entre deux trajets. Elle reste à la maison le jour où le client revient en magasin. La <strong className="font-semibold">carte de fidélité numérique</strong> règle ce problème en se logeant directement dans le smartphone, à l'endroit où le client garde déjà ses cartes bancaires et ses billets. En 2026, 80 % des consommateurs déclarent préférer ce format digital à la carte traditionnelle. Ce guide couvre la définition exacte de la <strong className="font-semibold">carte de fidélité numérique</strong>, son fonctionnement technique, ses avantages réels pour un commerce ou un indépendant, et les étapes concrètes pour en créer une.
            </p>

            {/* YouTube Video Embed */}
            <div className="my-8">
              <div
                className="relative w-full overflow-hidden rounded-2xl"
                style={{ aspectRatio: "16/9", ...imageStyle }}
              >
                <iframe
                  src="https://www.youtube-nocookie.com/embed/_mOe_msXbno?rel=0&modestbranding=1"
                  title="Carte de fidélité numérique : guide complet"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Section 1 */}
            <h2 className={h2}>Carte de fidélité numérique : définition et fonctionnement</h2>
            <p className={pText}>
              Une <strong className="font-semibold">carte de fidélité numérique</strong> est une carte intégrée à <strong className="font-semibold">Apple Wallet</strong> ou <strong className="font-semibold">Google Wallet</strong> (ou parfois à une application dédiée), qui remplace la carte en papier ou en plastique. Elle se met à jour automatiquement : points, tampons, statut. Le client n'a rien à faire pour que son solde reflète sa dernière visite.
            </p>
            <p className={pText}>
              Une confusion revient régulièrement dans les résultats de recherche sur ce sujet, et elle mérite d'être clarifiée dès le départ. Deux catégories d'outils se mélangent sous le même terme, alors qu'elles ne produisent pas le même résultat.
            </p>
            <p className={pText}>
              La première catégorie regroupe les <strong className="font-semibold">outils de création graphique</strong> : Canva, VistaPrint, et les générateurs de modèles imprimables. Ils produisent un visuel soigné, à imprimer ou à exporter en PDF. Le résultat reste une carte physique, avec un design moderne. Elle se tamponne à la main, se perd comme n'importe quelle carte papier, et ne communique aucune donnée au commerçant une fois imprimée.
            </p>
            <p className={pText}>
              La seconde catégorie regroupe les <strong className="font-semibold">plateformes de carte wallet fonctionnelle</strong> : elles produisent une vraie carte interactive, présente sur l'écran du téléphone, connectée à une base de données qui se met à jour en temps réel. C'est cette seconde catégorie qui correspond à la <strong className="font-semibold">carte de fidélité numérique</strong> au sens propre, et c'est elle que ce guide couvre.
            </p>
            <p className={pText}>
              Le fonctionnement technique tient en trois étapes. Le client scanne un QR code, en magasin, sur un ticket de caisse, ou sur un site web. La carte s'ajoute automatiquement à son <strong className="font-semibold">wallet mobile</strong> (Apple Wallet sur iPhone, Google Wallet sur Android), sans création de compte ni téléchargement d'application séparée. Elle se met à jour à chaque visite ou achat : un nouveau tampon, un solde de points actualisé, une notification si une récompense devient disponible.
            </p>
            <p className={pText}>
              Cette architecture technique explique une grande partie des avantages développés plus bas. Le commerçant n'a rien à installer côté caisse au-delà d'un terminal capable d'afficher ou d'imprimer un QR code, et le client n'a rien à gérer au-delà d'un scan initial de quelques secondes.
            </p>
            <p className={pText}>
              Pour choisir entre ce format et la carte physique selon le profil de votre activité, notre comparatif sur la carte numérique ou carte physique détaille les critères de décision dans chaque cas.
            </p>

            <img
              src={outilsImage}
              alt="Différence entre outils comme Canva et plateformes wallet avec création de visuels imprimés versus cartes digitales interactives"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* Section 2 */}
            <h2 className={h2}>Les avantages réels d'une carte de fidélité numérique</h2>
            <p className={pText}>
              Les bénéfices d'une <strong className="font-semibold">carte de fidélité numérique</strong> se répartissent entre le client et le commerçant, et chaque côté en tire un avantage différent.
            </p>
            <p className={pText}>
              Côté client, la carte est toujours disponible : pas d'oubli, pas de carte perdue au fond d'un tiroir. Le solde se met à jour automatiquement, sans intervention manuelle en caisse. Une notification arrive directement sur l'écran de verrouillage du téléphone quand une récompense devient accessible, sans que le client ait besoin d'ouvrir une application pour le savoir.
            </p>
            <p className={pText}>
              Côté commerçant, la carte collecte automatiquement des données comportementales : fréquence de visite, date du dernier achat, catégorie d'achat habituelle. Les coûts d'impression et de réassort des cartes physiques disparaissent. Et la carte devient un point de contact direct pour envoyer des messages liés à son programme de fidélité.
            </p>
            <p className={pText}>
              43 % des consommateurs déclarent préférer une carte intégrée à leur <strong className="font-semibold">wallet mobile</strong> plutôt que de télécharger une application dédiée. Cette préférence confirme l'avantage du format wallet face au format application : moins de friction à l'inscription, plus de chances que la carte soit réellement utilisée après le premier scan.
            </p>
            <p className={pText}>
              Une nuance honnête s'impose ici. La carte physique garde un avantage réel : le geste de la sortir du portefeuille crée une connexion émotionnelle immédiate avec la marque, que le numérique ne reproduit pas à l'identique. La <strong className="font-semibold">carte de fidélité numérique</strong> compense cet écart différemment : la notification sur l'écran de verrouillage crée un rappel actif, répété, que le papier ne peut pas générer seul une fois rangé dans un tiroir.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Avantage</th>
                    <th className="px-4 py-3 text-left font-semibold">Carte de fidélité numérique</th>
                    <th className="px-4 py-3 text-left font-semibold">Carte physique</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Disponibilité</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Toujours dans le téléphone</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Risque d'oubli ou de perte</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Mise à jour du solde</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Automatique</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Manuelle (tamponnage)</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Collecte de données</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Oui, à chaque scan</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non, ou très limitée</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Coût récurrent</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Abonnement plateforme</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Impression et réassort</td>
                  </tr>
                  <tr className="bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Connexion émotionnelle</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Notification répétée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Geste physique immédiat</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className={pText}>
              Un exemple concret illustre ces deux logiques côte à côte. Un commerce de quartier qui gère 200 clients réguliers avec une carte papier passe du temps à tamponner manuellement chaque visite, à réimprimer des lots de cartes vierges, et perd la trace des clients qui arrêtent de venir. Avec une <strong className="font-semibold">carte de fidélité numérique</strong>, le même commerce voit instantanément qui n'est pas revenu depuis 30 jours, sans tenir aucun registre, et peut lui envoyer un message ciblé en quelques clics.
            </p>

            <img
              src={notificationImage}
              alt="Notification wallet sur écran verrouillé avec statistique sur la préférence des consommateurs pour carte intégrée plutôt qu’application"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* Section 3 */}
            <h2 className={h2}>Comment créer une carte de fidélité numérique en 5 étapes</h2>
            <p className={pText}>
              Créer une <strong className="font-semibold">carte de fidélité numérique</strong> ne demande ni compétence technique ni équipe dédiée. Le processus suit une logique simple, applicable par un commerce local ou un indépendant en quelques heures.
            </p>
            <p className={pText}>
              La première étape consiste à choisir une <strong className="font-semibold">plateforme de wallet marketing</strong>. Elle gère la création de la carte, la base de contacts, et l'envoi des notifications depuis une seule interface.
            </p>
            <p className={pText}>
              La deuxième étape consiste à définir le type de programme : des tampons pour un commerce de passage, des points pour un programme cumulatif, ou un abonnement pour un service récurrent.
            </p>
            <p className={pText}>
              La troisième étape consiste à personnaliser le design : logo, couleurs de la marque, nom affiché sur la carte. Si votre budget est limité au démarrage, notre guide sur la carte de fidélité numérique gratuite explique comment franchir cette étape sans payer.
            </p>
            <p className={pText}>
              La quatrième étape consiste à générer le QR code d'inscription et à le placer aux points de contact pertinents : comptoir de caisse, table, vitrine, signature email.
            </p>
            <p className={pText}>
              La cinquième étape consiste à tester la carte sur son propre téléphone avant le lancement, pour vérifier que le scan fonctionne et que le message de bienvenue s'affiche correctement. Pour voir à quoi doit ressembler une carte une fois personnalisée, nos exemples et modèles de carte de fidélité numérique montrent des cas concrets par secteur d'activité.
            </p>

            <img
              src={etapesImage}
              alt="Processus en 5 étapes pour créer une carte de fidélité digitale avec choix plateforme personnalisation QR code et lancement"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* Section 4 */}
            <h2 className={h2}>Les données collectées et leur usage marketing</h2>
            <p className={pText}>
              Une <strong className="font-semibold">carte de fidélité numérique</strong> collecte, par défaut, un socle de données utile sans configuration complexe : prénom, date d'ajout à la carte, solde de points ou de tampons, date de dernière mise à jour, fréquence de visite. Aucune de ces données ne demande au client de remplir un formulaire : elles s'enregistrent automatiquement à chaque scan.
            </p>
            <p className={pText}>
              L'usage concret de ces données reste simple. Une segmentation basique distingue les contacts actifs des contacts inactifs depuis 30 ou 60 jours. Un message automatique peut se déclencher quand un seuil de points est atteint, ou quand l'inactivité dépasse une durée définie. Une boulangerie peut ainsi repérer en un coup d'œil les clients qui venaient chaque semaine et qui n'ont pas scanné leur carte depuis un mois, sans tenir aucun tableau de suivi manuel.
            </p>
            <p className={pText}>
              Le cadre réglementaire reste simple à respecter. L'inscription via le scan du QR code constitue un <strong className="font-semibold">opt-in explicite</strong> : le client choisit volontairement d'ajouter la carte à son wallet. Il garde le droit de la retirer à tout moment, directement depuis son téléphone, sans démarche auprès du commerçant. Cette traçabilité du consentement, intégrée nativement au fonctionnement de la carte, simplifie la conformité au RGPD par rapport à une base de contacts collectée manuellement sur papier.
            </p>

            {/* Section 5 */}
            <h2 className={h2}>Combien coûte une carte de fidélité numérique</h2>
            <p className={pText}>
              Le budget à prévoir varie selon la plateforme choisie et le volume de contacts géré, mais une fourchette réaliste se situe entre 15 € et plus de 50 € par mois. Trois facteurs font varier ce prix d'une offre à l'autre : le nombre de contacts actifs inclus, la présence ou non d'automatisations comportementales, et le quota de notifications envoyées chaque mois.
            </p>
            <p className={pText}>
              Le point rassurant : l'absence de coûts cachés à anticiper, contrairement à une carte physique qui implique impression, réassort régulier, et parfois formation du personnel à la gestion manuelle du programme. Pour un commerce qui démarre, l'investissement reste à plusieurs reprises inférieur au coût annuel cumulé de l'impression de plusieurs lots de cartes papier. Pour un comparatif détaillé des prix observés sur le marché et de ce que chaque palier débloque, notre guide sur combien coûte une carte de fidélité numérique entre dans le détail.
            </p>
            <p className={pText}>
              Unotif se positionne comme une plateforme accessible pour démarrer rapidement, sans équipe technique ni développement spécifique. La carte se crée depuis une interface simple, et les premiers messages peuvent partir le jour même du lancement.
            </p>
            <p className={pText}>
              Pour resituer la carte numérique parmi l'ensemble des solutions disponibles pour fidéliser sa clientèle, notre guide complet sur les <Link to="/meilleurs-outils-de-fidelisation-client" className={linkCls}>outils de fidélisation client</Link> replace cette carte dans une vue d'ensemble plus large.
            </p>

            {/* Section 6 */}
            <h2 className={h2}>Les erreurs fréquentes lors du lancement d'une carte de fidélité numérique</h2>
            <p className={pText}>
              Le lancement d'une <strong className="font-semibold">carte de fidélité numérique</strong> échoue rarement à cause de la technologie. Il échoue à cause de quelques erreurs récurrentes, faciles à éviter une fois identifiées.
            </p>
            <p className={pText}>
              La première erreur consiste à placer le QR code d'inscription à un seul endroit, généralement la caisse. Le taux d'adoption augmente nettement quand le QR code apparaît à plusieurs points de contact : ticket de caisse, vitrine, table, signature email, story sur les réseaux sociaux.
            </p>
            <p className={pText}>
              La deuxième erreur consiste à ne jamais communiquer après l'inscription. Un client qui scanne sa carte et ne reçoit aucun message pendant des semaines oublie son existence. Un message de bienvenue immédiat, suivi d'un premier message utile dans les deux semaines, ancre la carte dans l'usage réel du client.
            </p>
            <p className={pText}>
              La troisième erreur consiste à présenter la carte comme une obligation plutôt qu'un avantage. La formulation compte : "rejoignez notre programme et économisez sur vos prochains achats" fonctionne mieux qu'une simple mention technique du QR code à scanner.
            </p>
            <p className={pText}>
              La quatrième erreur consiste à viser un programme trop complexe dès le départ. Un système de tampons simple (10 achats, 1 offert) convertit mieux qu'un programme à plusieurs niveaux difficile à expliquer en quelques secondes en caisse.
            </p>

            <img
              src={erreursImage}
              alt="Liste des erreurs à éviter pour une carte de fidélité digitale avec QR code mal placé manque de suivi et programme complexe"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 mt-12">
              La <strong className="font-semibold">carte de fidélité numérique</strong> n'est plus une option secondaire en 2026. Elle est devenue l'attente par défaut d'une majorité de clients, et le canal le plus direct pour rester en contact avec eux sans dépendre d'un algorithme ou d'une boîte mail surchargée. Elle ne remplace pas la relation humaine construite en magasin : elle la prolonge sur l'écran que chacun consulte plusieurs dizaines de fois par jour. Trois critères suffisent pour bien choisir sa solution : la facilité de création, l'intégration native au wallet du téléphone, et un budget maîtrisé sans surprise. Une fois la carte créée, l'étape suivante consiste à l'animer dans le temps. Notre guide sur le <Link to="/notifications-push-wallet" className={linkCls}>push wallet</Link> explique comment envoyer des notifications directement liées à cette carte, sans application ni algorithme. Unotif permet de créer sa première carte de fidélité numérique et d'envoyer ses premières notifications en moins d'une journée.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CarteFideliteNumerique;
